import { useState } from 'react'

import { Box, Button, Icon, Link, Text } from '@factorialco/deprecated-design-system'
import { AnimatePresence } from 'framer-motion'
import { ReconciliationBankJournalLinesDataTableDocument } from 'generated/resources/datatable.graphql'
import { ReconciliationJournalLinesDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  ConfirmFinanceReconciliationSuggestionDocument,
  ConfirmFinanceReconciliationSuggestionMutation,
  JournalLineReconciliationDocument,
  JournalLineReconciliationFragment,
  ReconciliationFromFinancialDocumentSidePanelDocument,
  RejectFinanceReconciliationSuggestionDocument,
  RejectFinanceReconciliationSuggestionMutation,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { SourceReconciliationType } from 'modules/finance/components/ReconciliationForm/types'

import { Carousel } from './Carousel'
import { CarouselFooter } from './CarouselFooter'
import { LineItem } from './_LineItem'

type Props = {
  journalLine: JournalLineReconciliationFragment
  sourceType: SourceReconciliationType
  currency: string
  locale: string
  onHideSuggestions: () => void
}

export const ReconciliationSuggestions = ({
  journalLine,
  sourceType,
  currency,
  locale,
  onHideSuggestions,
}: Props) => {
  const [firstSuggestion, setFirstSuggestion] = useState(true)

  const [currentIndex, setCurrentIndex] = useState(0)

  const suggestions =
    sourceType === SourceReconciliationType.BankAccount
      ? journalLine.reconciliationSuggestions
      : journalLine.reconcilableReconciliationSuggestions

  const [confirm, { loading: loadingConfirm }] = useMutation(
    ConfirmFinanceReconciliationSuggestionDocument,
    {
      extractorFn: (data: ConfirmFinanceReconciliationSuggestionMutation) =>
        data.financeMutations.confirmFinanceReconciliationSuggestion,
      refetchQueries: [
        JournalLineReconciliationDocument,
        ReconciliationJournalLinesDataTableDocument,
        ReconciliationBankJournalLinesDataTableDocument,
        ReconciliationFromFinancialDocumentSidePanelDocument,
      ],
    }
  )

  const [reject, { loading: loadingReject }] = useMutation(
    RejectFinanceReconciliationSuggestionDocument,
    {
      extractorFn: (data: RejectFinanceReconciliationSuggestionMutation) =>
        data.financeMutations.rejectFinanceReconciliationSuggestion,
      refetchQueries: [JournalLineReconciliationDocument],
    }
  )

  const isLoading = loadingConfirm || loadingReject

  const confirmSuggestion = async () => {
    const currentSuggestion = suggestions[currentIndex]
    if (!currentSuggestion) return

    const unhandledResult = await confirm({
      variables: {
        id: currentSuggestion.id,
      },
    })

    const result = handleResult(unhandledResult, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('finance.reconciliations.suggestions.confirming_suggestion_error'),
      })
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.reconciliations.suggestions.confirming_suggestion_success'),
      })

      if (
        result.data.reconciliationSuggestion?.journalLine.fullyReconciledAt &&
        onHideSuggestions
      ) {
        onHideSuggestions()
      }

      setCurrentIndex((curr) => (curr > 0 ? curr - 1 : 0))
    }
  }

  const rejectSuggestion = async () => {
    const currentSuggestion = suggestions[currentIndex]

    if (!currentSuggestion) return
    setFirstSuggestion(false)

    const unhandledResult = await reject({
      variables: {
        id: currentSuggestion.id,
      },
    })

    const result = handleResult(unhandledResult, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('finance.reconciliations.suggestions.rejecting_suggestion_error'),
      })
    })

    if (result.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('finance.reconciliations.suggestions.rejecting_suggestion_success'),
      })
      setCurrentIndex((curr) => (curr > 0 ? curr - 1 : 0))
    }
  }

  if (!suggestions[0]) return null

  const contentAnimationVariants = {
    enter: () => {
      if (firstSuggestion) {
        return {
          opacity: 0,
          translateY: '-100%',
        }
      } else {
        return {
          opacity: 0,
          translateY: 1,
        }
      }
    },
    show: {
      opacity: 1,
      translateY: 0,
      translateX: 0,
    },
  }

  return (
    <Box paddingBottom="s16" gap="s16" flexDirection="row" justifyContent="spaceBetween">
      <Box width="full">
        <Box flexDirection="row" width="full" alignItems="center" justifyContent="spaceBetween">
          <Box flexDirection="row" gap="s4">
            <Text.Normal weight="semibold">
              {i18n.t('finance.reconciliations.suggestions.title')}
            </Text.Normal>
            <Icon.Medium icon="ai" color="grey800" />
          </Box>
          {onHideSuggestions && (
            <Link.Touchable onClick={onHideSuggestions}>
              {i18n.t('finance.reconciliations.suggestions.dismiss')}
            </Link.Touchable>
          )}
        </Box>
        <Text.Normal>{i18n.t('finance.reconciliations.suggestions.subtitle')}</Text.Normal>
      </Box>

      <Box width="full" overflowY="hidden">
        <Box
          background="white"
          paddingX="s24"
          paddingY="s16"
          flexDirection="row"
          gap="s32"
          style={{
            border: '1px solid #e2e2e5',
            borderRadius: '16px',
            zIndex: '99',
          }}
        >
          <Box>
            <Text.Normal color="grey800" size="200">
              {dateBackendDateTimeToUI(String(journalLine.journalEntry.publishedAt))}
            </Text.Normal>
            <Text.Normal color="grey800" size="200" weight="medium">
              {journalLine.creditAmountCents > 0
                ? i18n.t('finance.reconciliations.suggestions.credit')
                : i18n.t('finance.reconciliations.suggestions.debit')}
            </Text.Normal>
          </Box>
          <Box>
            <Text.Normal color="grey1000" size="200" weight="medium">
              {journalLine.journalEntry.description || ''}
            </Text.Normal>
            <Text.Normal color="grey800" size="200">
              {formattedAmount({
                amountCents: Number(journalLine.creditAmountCents + journalLine.debitAmountCents),
                currency,
                locale: locale,
              })}
            </Text.Normal>
          </Box>
        </Box>
        <AnimatePresence mode="popLayout">
          <Box.Motion
            flexGrow
            paddingTop="s0"
            initial="enter"
            animate="show"
            exit="exit"
            variants={contentAnimationVariants}
            transition={{ easing: 'easeInOut', duration: 0.5 }}
            maxHeight="full"
          >
            <Box
              gap="s16"
              background="grey100"
              style={{
                border: '1px solid #e2e2e5',
                borderBottomLeftRadius: '16px',
                borderBottomRightRadius: '16px',
                borderTop: 'none',
                marginTop: '-15px',
                padding: '32px 24px 16px 24px',
              }}
            >
              <Carousel
                index={currentIndex}
                footer={
                  <CarouselFooter
                    total={suggestions.length}
                    currentIndex={currentIndex}
                    setCurrentIndex={setCurrentIndex}
                    actionButtonsComponent={
                      <Box flexDirection="row" gap="s6">
                        <Button type="secondary" onClick={rejectSuggestion} disabled={isLoading}>
                          {i18n.t('finance.reconciliations.suggestions.reject')}
                        </Button>
                        <Button onClick={confirmSuggestion} disabled={isLoading}>
                          {i18n.t('finance.reconciliations.suggestions.confirm')}
                        </Button>
                      </Box>
                    }
                  />
                }
              >
                {suggestions.map((suggestion) => {
                  const suggestedJournalLine =
                    sourceType === SourceReconciliationType.BankAccount
                      ? suggestion.reconcilableJournalLine
                      : suggestion.journalLine

                  if (!suggestedJournalLine) return null

                  return (
                    <Box key={suggestion.id} gap="s12">
                      <Box justifyContent="spaceBetween" flexDirection="row">
                        <LineItem
                          label={i18n.t('finance.reconciliations.suggestions.description')}
                          value={suggestedJournalLine.journalEntry.description || '-'}
                          textAlign="left"
                        />
                      </Box>
                      <Box justifyContent="spaceBetween" flexDirection="row">
                        <LineItem
                          label={i18n.t('finance.reconciliations.suggestions.recorded_at')}
                          value={dateBackendDateTimeToUI(
                            suggestedJournalLine.journalEntry.publishedAt || ''
                          )}
                          textAlign="left"
                        />
                        <LineItem
                          label={
                            suggestedJournalLine.creditAmountCents > 0
                              ? i18n.t('finance.reconciliations.suggestions.credit')
                              : i18n.t('finance.reconciliations.suggestions.debit')
                          }
                          value={formattedAmount({
                            amountCents: Number(
                              suggestedJournalLine.creditAmountCents +
                                suggestedJournalLine.debitAmountCents
                            ),
                            currency,
                            locale: locale,
                          })}
                          textAlign="right"
                        />
                      </Box>
                    </Box>
                  )
                })}
              </Carousel>
            </Box>
          </Box.Motion>
        </AnimatePresence>
      </Box>
    </Box>
  )
}
